'use memo';

import { createFileRoute, useParams } from '@tanstack/react-router';
import { useCallback } from 'react';
import { Badge, type BadgeVariant } from '@/components/Badge';
import Icon from '@/components/Icon/Icon';
import { HStack } from '@/components/Layout';
import { ViewportLoader } from '@/components/Loading';
import { Text } from '@/components/Typography/Text';
import type { CFormSectionDetailLayoutSection } from '@/features/c-form/CFormSectionDetailLayout';
import { CFormSectionDetailLayout } from '@/features/c-form/CFormSectionDetailLayout';
import { isMultiSectionAllowedKey } from '@/features/device/multi-section-keys';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { useGetDeviceLifecycle } from '../-shared/-query';
import type { DeviceDetailSection } from '../-shared/-query/type';
import { ReadonlyAttributeRenderer } from '../-shared/ReadonlyAttributeRenderer';
export const Route = createFileRoute('/_staff/devices/$id/_layout/lifecycle/')({
  component: DeviceLifecycleTab
});
type StatusBadgeValue = {
  text: string;
  variant: BadgeVariant;
};
function findAttributeValue(section: CFormSectionDetailLayoutSection & {
  attributes: {
    attributeKey: string;
    value: unknown;
  }[];
}, keySuffix: string): string | null {
  const attr = section.attributes.find(a => a.attributeKey.endsWith(keySuffix));
  if (!attr || attr.value == null) return null;
  if (typeof attr.value === 'object' && 'text' in attr.value) {
    return String(attr.value.text);
  }
  return String(attr.value);
}
function findStatusBadge(section: CFormSectionDetailLayoutSection & {
  attributes: {
    attributeKey: string;
    value: unknown;
  }[];
}): StatusBadgeValue | null {
  const attr = section.attributes.find(a => a.attributeKey.endsWith('Status'));
  if (!attr || attr.value == null) return null;
  if (typeof attr.value === 'object') {
    return {
      text: 'text' in attr.value ? String(attr.value.text) : String(attr.value),
      variant: 'variant' in attr.value && typeof attr.value.variant === 'string' ? attr.value.variant as BadgeVariant : 'neutralMuted'
    };
  }
  return {
    text: String(attr.value),
    variant: 'neutralMuted'
  };
}
function formatDateValue(isoDate: string | null, locale: string): string {
  if (!isoDate) return '—';
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return isoDate;
  return date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}
function LifecycleAccordionTrigger({
  section,
  locale
}: {
  section: DeviceDetailSection;
  locale: string;
}) {
  const statusBadge = findStatusBadge(section);
  const startDate = findAttributeValue(section, 'StartDate') ?? findAttributeValue(section, 'Timestamp');
  const endDate = findAttributeValue(section, 'EndDate');
  const dateLabel = endDate ? `${formatDateValue(startDate, locale)} – ${formatDateValue(endDate, locale)}` : formatDateValue(startDate, locale);
  return <HStack className="w-full items-center justify-between">
      <HStack className="items-center gap-2">
        <Icon iconName="CalendarDaysIcon" className="w-4 h-4 shrink-0" />
        <Text variant="p" className="font-semibold text-sm whitespace-nowrap">
          {dateLabel}
        </Text>
      </HStack>
      {statusBadge && <Badge variant={statusBadge.variant} className="whitespace-nowrap">
          {statusBadge.text}
        </Badge>}
    </HStack>;
}
function DeviceLifecycleTab() {
  const {
    i18n
  } = useTranslation(defaultNS);
  const {
    id
  } = useParams({
    from: '/_staff/devices/$id/_layout/lifecycle/'
  });
  const {
    data,
    isPending,
    error
  } = useGetDeviceLifecycle(id);
  const renderSectionContent = useCallback((section: DeviceDetailSection) => <ReadonlyAttributeRenderer attributes={section.attributes} sectionKey={String(section.sectionKey)} />, []);
  const renderAccordionTrigger = useCallback((section: DeviceDetailSection) => <LifecycleAccordionTrigger section={section} locale={i18n.language} />, [i18n.language]);
  if (!data) {
    return <ViewportLoader />;
  }
  return <CFormSectionDetailLayout sections={data?.sections} isLoading={isPending || !data} error={error} linkTo="." itemLayout="card" groupBySectionKey groupNavigationBySectionParent isMultiSectionAllowed={isMultiSectionAllowedKey} multiSectionDisplayType="accordion" renderAccordionTrigger={renderAccordionTrigger} renderSectionContent={renderSectionContent} />;
}